import React, { createContext, useEffect, useReducer, ReactNode } from 'react';
import { AuthService } from '../services/authService';
import { AuthState, AuthUser } from '../types/auth.types';

// ─── Estado y acciones ──────────────────────────────────────────────────────

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: AuthUser | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  isInstructor: false,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isAdmin: action.payload?.profile?.role === 'admin',
        isInstructor: action.payload?.profile?.role === 'instructor',
        isLoading: false,
        error: null,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// ─── Contexto ────────────────────────────────────────────────────────────────

interface AuthContextType extends AuthState {
  sendOtp: (email: string) => Promise<boolean>;
  verifyOtp: (email: string, code: string) => Promise<boolean>;
  updateFullName: (fullName: string) => Promise<boolean>;
  deleteMyAccount: () => Promise<boolean>;
  signOut: () => Promise<void>;
  clearError: () => void;
  getToken: () => string | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    let cancelled = false;
    let loadedUserId: string | null = null;

    /** Carga el perfil al abrir sesión; en una simple renovación del token no hace nada. */
    const applySession = async (accessToken: string | null) => {
      const sessionUser = accessToken ? AuthService.userFromAccessToken(accessToken) : null;
      if (!accessToken || !sessionUser) {
        loadedUserId = null;
        if (!cancelled) dispatch({ type: 'SET_USER', payload: null });
        return;
      }
      if (sessionUser.id === loadedUserId) return;

      loadedUserId = sessionUser.id;
      // Primer acceso: el perfil local aún no existe y se crea con sync.
      // Si cargar el perfil falla por lo que sea, el usuario entra igualmente
      // (sin perfil): dejarlo sin SET_USER lo deja clavado en la pantalla de
      // login con el botón en "Verificando...".
      let profile: AuthUser['profile'] = null;
      try {
        profile =
          (await AuthService.getMyProfile(accessToken)) ??
          (AuthService.getToken() ? await AuthService.syncProfile(accessToken) : null);
      } catch {
        profile = null;
      }
      // getMyProfile cierra la sesión si el backend responde 401
      if (cancelled || !AuthService.getToken()) return;
      dispatch({ type: 'SET_USER', payload: { ...sessionUser, profile } });
    };

    // Suscribirse antes de inicializar para no perder la renovación inicial
    const unsubscribe = AuthService.onSessionChange((session) => {
      void applySession(session?.accessToken ?? null);
    });
    void AuthService.initSession()
      .then((session) => applySession(session?.accessToken ?? null))
      .catch(() => dispatch({ type: 'SET_USER', payload: null }));

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Sin SET_LOADING: App muestra el spinner global con isLoading y desmontaría
  // LoginPage, perdiendo el paso en el que está el usuario.
  const sendOtp = async (email: string): Promise<boolean> => {
    dispatch({ type: 'CLEAR_ERROR' });
    try {
      await AuthService.sendOtp(email);
      return true;
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: (err as Error).message });
      return false;
    }
  };

  const verifyOtp = async (email: string, code: string): Promise<boolean> => {
    dispatch({ type: 'CLEAR_ERROR' });
    try {
      await AuthService.verifyOtp(email, code);
      // onSessionChange se encarga de cargar el perfil
      return true;
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: (err as Error).message });
      return false;
    }
  };

  const updateFullName = async (fullName: string): Promise<boolean> => {
    dispatch({ type: 'CLEAR_ERROR' });
    try {
      const token = AuthService.getToken();
      if (!token || !state.user) throw new Error('No hay sesión activa.');
      const profile = await AuthService.updateMyName(token, fullName);
      dispatch({ type: 'SET_USER', payload: { ...state.user, profile } });
      return true;
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: (err as Error).message });
      return false;
    }
  };

  const deleteMyAccount = async (): Promise<boolean> => {
    dispatch({ type: 'CLEAR_ERROR' });
    try {
      const token = AuthService.getToken();
      if (!token) throw new Error('No hay sesión activa.');
      await AuthService.deleteMyAccount(token);
      await AuthService.signOut();
      dispatch({ type: 'SET_USER', payload: null });
      return true;
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: (err as Error).message });
      return false;
    }
  };

  const signOut = async () => {
    try {
      await AuthService.signOut();
      dispatch({ type: 'SET_USER', payload: null });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: (err as Error).message });
    }
  };

  const getToken = (): string | null => AuthService.getToken();

  const clearError = () => dispatch({ type: 'CLEAR_ERROR' });

  return (
    <AuthContext.Provider
      value={{
        ...state,
        sendOtp,
        verifyOtp,
        updateFullName,
        deleteMyAccount,
        signOut,
        clearError,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
